/**
 * Projects: folders that group explorations, selections and reproductions.
 *
 * The API (backend ``projects`` app):
 *   GET    api/project/                 { projects: [...], unfiled: { *_count } }
 *   POST   api/project/                 { name, description }
 *   PATCH  api/project/<id>/            { name?, description? }
 *   DELETE api/project/<id>/            items are unfiled, never deleted
 *   GET    api/project/<id>/items/      { project, explorations, selections, reproductions }
 *   GET    api/project/unfiled/items/   the same for the items of no project
 *   POST   api/project/move/            { items: [{ kind, id }], project: <id> | null }
 *
 * An item is in at most one project. Items of no project are "unfiled":
 * they show under the Unfiled entry of the Projects page and, as before, in
 * their module's overview list.
 */
import axios from "axios";
import { withListScope } from "../components/ListScopeToggle";

export const PROJECTS_ROUTE = "/projects";
/** URL segment and id of the built-in "no project" bucket. */
export const UNFILED = "unfiled";
export const ITEM_KINDS = ["exploration", "selection", "reproduction"];
/** The drag payload type of cards dragged between projects. */
export const DRAG_MIME = "application/x-oraclase-project-items";

const EMPTY_COUNTS = { exploration_count: 0, selection_count: 0, reproduction_count: 0 };

/** The Projects page with the given project (or the unfiled bucket) open. */
export function projectRoute(projectId) {
    return `${PROJECTS_ROUTE}/${projectId || UNFILED}`;
}

export async function fetchProjects() {
    const res = await axios.get("api/project/");
    return {
        projects: Array.isArray(res?.data?.projects) ? res.data.projects : [],
        unfiled: { ...EMPTY_COUNTS, ...(res?.data?.unfiled || {}) },
    };
}

export async function createProject(name, description = "") {
    const res = await axios.post("api/project/", { name, description });
    return res.data;
}

export async function updateProject(projectId, patch) {
    const res = await axios.patch(`api/project/${projectId}/`, patch);
    return res.data;
}

export async function deleteProject(projectId) {
    await axios.delete(`api/project/${projectId}/`);
}

/** The items of one project (``UNFILED`` for the bucket), narrowed by the
 *  Mine/All scope like the overview lists. */
export async function fetchProjectItems(projectId, scope) {
    const url = projectId === UNFILED
        ? "api/project/unfiled/items/"
        : `api/project/${projectId}/items/`;
    const res = await axios.get(withListScope(url, scope));
    return {
        project: res?.data?.project || null,
        explorations: res?.data?.explorations || [],
        selections: res?.data?.selections || [],
        reproductions: res?.data?.reproductions || [],
    };
}

/** Files ``items`` ([{ kind, id }]) into ``projectId``; null or UNFILED
 *  unfiles them. */
export async function moveItems(items, projectId) {
    const target = projectId && projectId !== UNFILED ? projectId : null;
    const res = await axios.post("api/project/move/", { items, project: target });
    return res.data;
}

// ---------------------------------------------------------------------------
// Creating inside a project: the New pages carry ``?project=<id>``.
// ---------------------------------------------------------------------------

/** ``route`` with ``?project=<id>`` appended (unchanged for no project). */
export function withProjectParam(route, projectId) {
    if (!projectId || projectId === UNFILED) return route;
    const separator = route.includes("?") ? "&" : "?";
    return `${route}${separator}project=${encodeURIComponent(projectId)}`;
}

/** The project id of a ``?project=`` query string, or null. */
export function projectIdFromSearch(search) {
    const value = new URLSearchParams(search || "").get("project");
    const trimmed = value ? value.trim() : "";
    return trimmed.length > 0 ? trimmed : null;
}

// ---------------------------------------------------------------------------
// Drag payload of the cards
// ---------------------------------------------------------------------------

const isItem = (entry) =>
    entry && typeof entry === "object" && ITEM_KINDS.includes(entry.kind) && Boolean(entry.id);

/** Puts ``items`` ([{ kind, id }]) on a drag's dataTransfer. */
export function writeDragItems(dataTransfer, items) {
    if (!dataTransfer) return;
    const payload = (items || []).filter(isItem).map(({ kind, id }) => ({ kind, id }));
    try {
        dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
        // Dropping outside the app pastes something readable.
        dataTransfer.setData("text/plain", payload.map((item) => item.id).join(", "));
        dataTransfer.effectAllowed = "move";
    } catch (_error) {
        // A dataTransfer without setData (jsdom without a polyfill): no payload.
    }
}

/** True while a card drag is over a drop target (a file drag is not one). */
export function hasDragItems(dataTransfer) {
    const types = dataTransfer?.types;
    if (!types) return false;
    return Array.from(types).includes(DRAG_MIME);
}

/** The items of a drop, or [] when the drag was not a card drag. */
export function readDragItems(dataTransfer) {
    try {
        const raw = dataTransfer?.getData?.(DRAG_MIME);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter(isItem) : [];
    } catch (_error) {
        return [];
    }
}

/** The server's message of a failed project request, or ``fallback``. */
export function projectErrorMessage(error, fallback) {
    const data = error?.response?.data;
    if (!data) return fallback;
    if (typeof data.error === "string") return data.error;
    if (typeof data.message === "string") return data.message;
    // DRF field errors: { name: ["A project with this name exists."] }.
    for (const value of Object.values(data)) {
        if (Array.isArray(value) && typeof value[0] === "string") return value[0];
        if (typeof value === "string") return value;
    }
    return fallback;
}
